import { type DrawableMixin } from "../objects/drawable_mixin"
import PathObject from "../objects/path"
import Form from "./base"
import Calc from "@/common/util/calculator"

export default class RoundedTop extends Form {
  protected path(width: number, height: number, rounding?: number) {
    const ah = height * 0.1
    let cmds = []

    if (rounding) {
      const roundDiff = rounding / 2
      const curviness = 0.3
      cmds = [this.pathMoveTo(-(width / 2), -(height / 2) + ah + roundDiff)] // UL

      cmds.push(
        this.pathCurveAbs(
          -(width / 2),
          -(height / 2) + ah + roundDiff,
          -(width / 2),
          -(height / 2) + ah + curviness * roundDiff,
          -(width / 2) + roundDiff,
          -(height / 2) + ah
        )
      ) // Upper left rounding
      cmds.push(this.pathCurveAbs(-(width / 4), -(height / 2), 0, -(height / 2), 0, -(height / 2))) // UC1
      cmds.push(
        this.pathCurveAbs(
          width / 4,
          -(height / 2),
          width / 2 - roundDiff,
          -(height / 2) + ah,
          width / 2 - roundDiff,
          -(height / 2) + ah
        )
      ) // UC2
      cmds.push(
        this.pathCurveAbs(
          width / 2 - roundDiff,
          -(height / 2) + ah,
          width / 2,
          -(height / 2) + ah + curviness * roundDiff,
          width / 2,
          -(height / 2) + ah + roundDiff
        )
      ) // Upper right rounding
      cmds.push(this.pathLineToAbs(width / 2, height / 2 - ah - roundDiff)) // LR
      cmds.push(
        this.pathCurveAbs(
          width / 2,
          height / 2 - ah - roundDiff,
          width / 2,
          height / 2 - ah - curviness * roundDiff,
          width / 2 - roundDiff,
          height / 2 - ah
        )
      ) // Lower right rounding
      cmds.push(this.pathCurveAbs(width / 4, height / 2, 0, height / 2, 0, height / 2)) // LC1
      cmds.push(
        this.pathCurveAbs(
          -(width / 4),
          height / 2,
          -(width / 2) + roundDiff,
          height / 2 - ah,
          -(width / 2) + roundDiff,
          height / 2 - ah
        )
      ) // LC2
      cmds.push(
        this.pathCurveAbs(
          -(width / 2) + roundDiff,
          height / 2 - ah,
          -(width / 2),
          height / 2 - ah - curviness * roundDiff,
          -(width / 2),
          height / 2 - ah - roundDiff
        )
      ) // Lower left rounding
      cmds.push(this.pathClose())
    } else {
      const [side, rest] = this.snappableCommands(width, height)
      rest.shift()
      cmds = side.concat(rest)
    }

    return new PathObject(cmds.join(" "), this.defaultParams)
  }

  protected snappablePaths(width: number, height: number): [DrawableMixin, DrawableMixin] {
    const [side, rest] = this.snappableCommands(width, height)

    return [new PathObject(side.join(""), this.defaultParams), new PathObject(rest.join(""), this.defaultParams)]
  }

  private snappableCommands(width: number, height: number): [string[], string[]] {
    const ah = height * 0.1

    const side = [this.pathMoveTo(-(width / 2), height / 2 - ah)]
    side.push(this.pathLineToAbs(-(width / 2), -(height / 2) + ah))

    const rest = [this.pathMoveTo(-(width / 2), -(height / 2) + ah)] // UL

    rest.push(this.pathCurveAbs(-(width / 4), -(height / 2), 0, -(height / 2), 0, -(height / 2))) // UC1
    rest.push(this.pathCurveAbs(width / 4, -(height / 2), width / 2, -(height / 2) + ah, width / 2, -(height / 2) + ah)) // UC2
    rest.push(this.pathLineToAbs(width / 2, height / 2 - ah)) // LR
    rest.push(this.pathCurveAbs(width / 4, height / 2, 0, height / 2, 0, height / 2)) // LC1
    rest.push(this.pathCurveAbs(-(width / 4), height / 2, -(width / 2), height / 2 - ah, -(width / 2), height / 2 - ah)) // LC2

    return [side, rest]
  }

  protected get cutlineRounding() {
    return this.printCutlineRounding(this.calcRounding())
  }

  private calcRounding() {
    if (["aluminium_sign", "acryl_sign", "acryl_alu_sign"].includes(this.signType)) {
      return Calc.mmToPx(1)
    }

    return 0
  }
}
